/**
 * ConnectionIndicator
 *
 * A small colored dot that lives inside the PortalHeader, next to the
 * notification bell.  It provides an always-visible, zero-layout-shift
 * indication of WebSocket health:
 *
 *   🟢  green        — authenticated / connected
 *   🟡  amber pulse  — reconnecting / connecting / authenticating
 *   🔴  red          — failed / offline
 *   ⚪  grey         — disconnected (idle)
 *
 * Hovering shows a tooltip with extra detail (latency, attempt count, etc.).
 */

import React, { useMemo } from "react";
import { useWebSocket } from "../../contexts/WebSocketProvider";
import type { ConnectionHealth } from "../../services/socketService";
import "./ConnectionIndicator.css";

/* ── Pure helper (exported for tests) ──────────────────────────────────── */

export type IndicatorColor = "green" | "amber" | "red" | "grey";

export interface IndicatorConfig {
  color: IndicatorColor;
  pulse: boolean;
  tooltip: string;
}

export function getIndicatorConfig(health: ConnectionHealth): IndicatorConfig {
  const {
    state,
    latencyMs,
    reconnectAttempt,
    maxReconnectAttempts,
    isNetworkOnline,
    isLatencyElevated,
  } = health;

  if (!isNetworkOnline) {
    return { color: "red", pulse: false, tooltip: "You are offline" };
  }

  switch (state) {
    case "authenticated": {
      // Debounced: latencyMs must have read slow on several consecutive
      // probes (see socketService's SLOW_LATENCY_CONSECUTIVE_SAMPLES), not
      // just this one sample, so a single blip on a mobile connection
      // doesn't flip the indicator.
      if (isLatencyElevated) {
        return {
          color: "amber",
          pulse: false,
          tooltip: `Slow connection (${latencyMs}ms)`,
        };
      }
      const latencyLabel = latencyMs != null ? ` · ${latencyMs}ms` : "";
      return {
        color: "green",
        pulse: false,
        tooltip: `Connected${latencyLabel}`,
      };
    }

    case "reconnecting":
      return {
        color: "amber",
        pulse: true,
        tooltip: `Reconnecting… attempt ${reconnectAttempt} of ${maxReconnectAttempts}`,
      };

    case "connecting":
    case "connected":
      return { color: "amber", pulse: true, tooltip: "Connecting…" };

    case "authenticating":
      return { color: "amber", pulse: true, tooltip: "Authenticating…" };

    case "failed":
      return {
        color: "red",
        pulse: false,
        tooltip: "Connection failed — click to retry",
      };

    case "disconnected":
    default:
      return { color: "grey", pulse: false, tooltip: "Disconnected" };
  }
}

/* ── Component ─────────────────────────────────────────────────────────── */

export const ConnectionIndicator: React.FC = () => {
  const { health, reconnect } = useWebSocket();
  const config = useMemo(() => getIndicatorConfig(health), [health]);

  const handleClick = () => {
    if (health.state === "failed") {
      reconnect();
    }
  };

  return (
    <button
      className={[
        "connection-indicator",
        `connection-indicator--${config.color}`,
        config.pulse ? "connection-indicator--pulse" : "",
        health.state === "failed" ? "connection-indicator--clickable" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={handleClick}
      title={config.tooltip}
      aria-label={config.tooltip}
      data-testid="connection-indicator"
      type="button"
    />
  );
};

export default ConnectionIndicator;
